// components/order/OrderDetailPanel.jsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Button,
    CircularProgress,
    Chip,
    Grid,
    Paper,
    MenuItem,
    Select as MuiSelect,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import SlidePanel from "@/theme/partials/slidepanel";
import { useOrderDetail } from "@/hooks/order/useOrderDetail";
import { useTranslation } from "@/hooks/useTranslation";
import Icon from "@/theme/vectors/bundle";
import { formatDateTime, formatCurrency, formatPhoneNumber } from "@/utils/order/helpers";
import { ORDER_STATUSES, ORDER_STATUS_COLORS } from "@/utils/order/constants";

const OrderDetailPanel = ({ open, onClose, orderId, onStatusChange }) => {
    const { t } = useTranslation();
    const { orderDetail, loading, error, fetchOrderDetail } = useOrderDetail();
    const [newStatus, setNewStatus] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);

    // Tải thông tin chi tiết đơn hàng khi panel mở và có orderId
    useEffect(() => {
        if (open && orderId) {
            fetchOrderDetail(orderId);
        }
    }, [open, orderId, fetchOrderDetail]);

    // Cập nhật newStatus khi orderDetail thay đổi
    useEffect(() => {
        if (orderDetail?.order) {
            console.log('Trạng thái đơn hàng từ API:', orderDetail.order.order_status);
            // Chuẩn hóa trạng thái để đảm bảo khớp với ORDER_STATUSES (viết hoa)
            const normalizedStatus = orderDetail.order.order_status ? orderDetail.order.order_status.toUpperCase() : "";
            setNewStatus(normalizedStatus);
            console.log('Đã cập nhật newStatus thành:', normalizedStatus);
        }
    }, [orderDetail]);

    // Xử lý khi thay đổi trạng thái đơn hàng từ dropdown
    const handleStatusSelectChange = async (e) => {
        const selectedStatus = e.target.value;
        if (selectedStatus === (orderDetail?.order?.order_status || "")) return;
        
        setStatusUpdating(true);
        setNewStatus(selectedStatus);
        try {
            await onStatusChange(orderId, selectedStatus);
            // Có thể thêm thông báo thành công nếu cần
        } catch (error) {
            // Xử lý lỗi nếu cần
            console.error("Error updating status:", error);
        } finally {
            setStatusUpdating(false);
        }
    };

    // Hiển thị chip trạng thái với màu tương ứng
    const renderStatusChip = (status) => {
        if (!status) return null;
        
        // Chuẩn hóa trạng thái để phù hợp với ORDER_STATUSES
        const normalizedStatus = status.toUpperCase();
        const color = ORDER_STATUS_COLORS[normalizedStatus] || "default";
        
        // Xác định trạng thái cần có chữ màu trắng
        const needsWhiteText = normalizedStatus === "CANCELLED" || normalizedStatus === "SHIPPING";
        
        return (
            <Chip
                label={t(`order.statusOptions.${normalizedStatus.toLowerCase()}`)}
                color={color}
                variant="filled"
                size="small"
                sx={{ 
                    fontWeight: 600,
                    minWidth: '100px',
                    color: needsWhiteText ? '#fff' : undefined,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    '& .MuiChip-label': {
                        padding: '0 8px'
                    }
                }}
            />
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ p: 3, color: "error.main" }}>
                    <Typography>{t("common.errorLoading")}</Typography>
                    <Typography variant="body2">{t(error)}</Typography>
                </Box>
            );
        }

        if (!orderDetail || !orderDetail.order) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography>{t("order.noOrderSelected")}</Typography>
                </Box>
            );
        }

        const order = orderDetail.order;
        const items = orderDetail.items || [];

        return (
            <Box sx={{ 
                p: 2, 
                bgcolor: 'background.paper',
                borderRadius: 1
            }}>
                {/* Thông tin cơ bản */}
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                        content: '""',
                        display: 'inline-block',
                        width: '4px',
                        height: '18px',
                        backgroundColor: 'primary.main',
                        borderRadius: '2px',
                        marginRight: '8px'
                    }
                }}>
                    {t("order.basicInfo")}
                </Typography>
                
                <Box sx={{
                    backgroundColor: 'background.default',
                    padding: 2,
                    borderRadius: 1,
                    mb: 3
                }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.orderId")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: "medium", mb: 1 }}
                            >
                                {order.id}
                            </Typography>

                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.createdAt")}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {formatDateTime(order.created_at)}
                            </Typography>

                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.paymentMethod")}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {order.payment_method || "-"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.status")}
                            </Typography>
                            <Box
                                sx={{
                                    mb: 1,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {renderStatusChip(order.order_status)}
                            </Box>

                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.totalAmount")}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: "bold",
                                    mb: 1,
                                    color: "primary.main",
                                    textAlign: "left",
                                }}
                            >
                                {formatCurrency(order.total_amount)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Thông tin khách hàng */}
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                        content: '""',
                        display: 'inline-block',
                        width: '4px',
                        height: '18px',
                        backgroundColor: 'primary.main',
                        borderRadius: '2px',
                        marginRight: '8px'
                    }
                }}>
                    {t("order.customerInfo")}
                </Typography>

                <Box sx={{
                    backgroundColor: 'background.default',
                    padding: 2,
                    borderRadius: 1,
                    mb: 3
                }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.customerName")}
                            </Typography>
                            <Typography variant="body1" sx={{ 
                                mb: 1, 
                                fontWeight: "medium" 
                            }}>
                                {order.customer_name}
                            </Typography>

                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.customerPhone")}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {formatPhoneNumber(order.customer_phone)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t("order.fields.customerAddress")}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {order.delivery_address || "-"}
                            </Typography>

                            {order.notes && (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        {t("order.fields.notes")}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        {order.notes}
                                    </Typography>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Danh sách sản phẩm */}
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                        content: '""',
                        display: 'inline-block',
                        width: '4px',
                        height: '18px',
                        backgroundColor: 'primary.main',
                        borderRadius: '2px',
                        marginRight: '8px'
                    }
                }}>
                    {t("order.items")}
                </Typography>

                <TableContainer component={Paper} sx={{ 
                    mb: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'background.default' }}>
                                <TableCell width="50%">
                                    {t("order.fields.productInfo")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("order.fields.quantity")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("order.fields.unitPrice")}
                                </TableCell>
                                <TableCell align="right">
                                    {t("order.fields.totalPrice")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items && items.length > 0 ?
                                items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {item.product_name || t("order.unknownProduct")}
                                            </Typography>
                                            
                                            {/* Hiển thị tên biến thể nếu có (cải thiện trải nghiệm) */}
                                            {item.variant_name && (
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    {item.variant_name}
                                                </Typography>
                                            )}
                                            
                                            {/* Display attribute values */}
                                            {(() => {
                                                // First check for API response structure 
                                                if (Array.isArray(item.attributeValues)) {
                                                    return (
                                                        <Box sx={{ mt: 1 }}>
                                                            {item.attributeValues.map((attr) => (
                                                                <Chip 
                                                                    key={attr.id} 
                                                                    label={`${attr.attribute_name}: ${attr.value}`}
                                                                    size="small"
                                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    );
                                                }
                                                
                                                // Then check if it's an object with internal attribute values
                                                if (Array.isArray(item.attribute_values)) {
                                                    return (
                                                        <Box sx={{ mt: 1 }}>
                                                            {item.attribute_values.map((attr) => (
                                                                <Chip 
                                                                    key={attr.id} 
                                                                    label={`${attr.attribute_name}: ${attr.value}`}
                                                                    size="small"
                                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    );
                                                }
                                                
                                                return null;
                                            })()}
                                        </TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(item.unit_price)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(item.total_price)}
                                        </TableCell>
                                    </TableRow>
                                )) :
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        {t("order.noItems")}
                                    </TableCell>
                                </TableRow>
                            }
                            {items && items.length > 0 && (
                                <TableRow sx={{ backgroundColor: 'background.default' }}>
                                    <TableCell
                                        colSpan={3}
                                        align="right"
                                        sx={{ 
                                            fontWeight: "bold",
                                            color: 'text.primary'
                                        }}
                                    >
                                        {t("order.fields.totalAmount")}:
                                    </TableCell>
                                    <TableCell 
                                        align="right" 
                                        sx={{ 
                                            fontWeight: "bold",
                                            color: 'primary.main',
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {formatCurrency(order.total_amount)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Cập nhật trạng thái */}
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                        content: '""',
                        display: 'inline-block',
                        width: '4px',
                        height: '18px',
                        backgroundColor: 'primary.main',
                        borderRadius: '2px',
                        marginRight: '8px'
                    }
                }}>
                    {t("order.updateStatus")}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {Object.keys(ORDER_STATUSES).map((key) => {
                        // Đảm bảo so sánh không phân biệt hoa thường
                        const isCurrentStatus = key === (newStatus || "");
                        // Lấy màu từ ORDER_STATUS_COLORS cho trạng thái hiện tại
                        const color = isCurrentStatus ? ORDER_STATUS_COLORS[key] : 'default';
                        // Xác định trạng thái cần có chữ màu trắng
                        const needsWhiteText = isCurrentStatus && (key === "CANCELLED" || key === "SHIPPING");
                        
                        return (
                            <Chip
                                key={key}
                                label={t(`order.statusOptions.${key.toLowerCase()}`)}
                                color={color}
                                variant={isCurrentStatus ? "filled" : "outlined"}
                                size="medium"
                                onClick={() => {
                                    if (key !== newStatus && !statusUpdating) {
                                        setStatusUpdating(true);
                                        setNewStatus(key);
                                        onStatusChange(orderId, key)
                                            .finally(() => {
                                                setStatusUpdating(false);
                                            });
                                    }
                                }}
                                sx={{ 
                                    fontWeight: isCurrentStatus ? 600 : 400,
                                    minWidth: '120px',
                                    cursor: statusUpdating ? 'not-allowed' : 'pointer',
                                    borderColor: 'rgba(0, 0, 0, 0.12)',
                                    color: needsWhiteText ? '#fff' : (isCurrentStatus ? 'inherit' : 'text.primary'),
                                    // Tăng cường độ tương phản cho trạng thái được chọn
                                    ...(isCurrentStatus && {
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                    })
                                }}
                            />
                        );
                    })}
                    {statusUpdating && (
                        <CircularProgress
                            size={24}
                            sx={{
                                ml: 1,
                                color: 'primary.main'
                            }}
                        />
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <SlidePanel 
            isOpen={open} 
            onClose={onClose}
            title={orderDetail?.order ? 
                `${orderDetail.order.id} - ${orderDetail.order.customer_name} - ${formatPhoneNumber(orderDetail.order.customer_phone)}` 
                : ''
            }
        >
            {renderContent()}
        </SlidePanel>
    );
};

export default OrderDetailPanel;
